/**
 * Chess utilities for the analysis pipeline:
 * - uciListToSan: convert a UCI move list to SAN from a starting FEN
 * - computeContinuationEvals: shallow eval each step of a best-move continuation
 */
import { applyLegalMove, applySanMove, isCheckmate, type EvaluationScore } from "@chessla/shared";
import type { StockfishWorker } from "./stockfish-worker";

/** Replays UCI moves from a FEN and returns each move in SAN notation. */
export function uciListToSan(fen: string, uciMoves: string[]): string[] {
  const sans: string[] = [];
  let current = fen;
  for (const uci of uciMoves) {
    const move = applyLegalMove(current, uci);
    if (!move) break;
    sans.push(move.san);
    current = move.fen;
  }
  return sans;
}

/**
 * Evaluates each step of a best-move continuation at shallow depth.
 * Used to populate the ◀▶ step navigator on the inline board.
 */
export async function computeContinuationEvals(
  engine: Pick<StockfishWorker, "evaluate">,
  startingFen: string,
  bestMoveSan: string,
  continuationSan: string[],
  maxSteps: number,
  depth = 10,
): Promise<EvaluationScore[]> {
  const evals: EvaluationScore[] = [];
  const analyzedColor = startingFen.split(" ")[1];
  const bestMove = applySanMove(startingFen, bestMoveSan);
  if (!bestMove) return [];
  let current = bestMove.fen;
  for (const san of continuationSan.slice(0, maxSteps)) {
    const move = applySanMove(current, san);
    if (!move) break;
    current = move.fen;
    // Stockfish scores from the side-to-move perspective. Persist every
    // checkpoint from the original mover's perspective so adjacent plies do
    // not alternate signs and the backend can evaluate line stability.
    const multiplier = current.split(" ")[1] === analyzedColor ? 1 : -1;
    if (isCheckmate(current)) {
      evals.push({ cp: null, mate: -multiplier });
      continue;
    }
    const evaluation = await engine.evaluate(current, depth, 1);
    evals.push({
      cp: evaluation.mate == null ? (evaluation.cp === 0 ? 0 : evaluation.cp * multiplier) : null,
      mate: evaluation.mate == null ? null : evaluation.mate * multiplier,
    });
  }
  return evals;
}

export const legacyContinuationEvals = (scores: readonly EvaluationScore[]): number[] | null =>
  scores.every((score) => score.mate == null && score.cp != null) ? scores.map((score) => score.cp!) : null;
